import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AuthenticationService } from 'app/auth/service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';

import { User } from 'app/auth/models';

import { SubscriptionService } from 'app/auth/service/subscription.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SocketIoService } from 'app/main/services/io.service';
import { isSender, roleName } from 'app/main/helpers/userData.helper';
import { CustomerService } from 'app/main/services/customer.service';
import { now } from 'app/main/helpers/timezone.helper';
import { WEBSOCKET_EVENT } from 'app/main/shared/enums/WebsocketEvent.enum';
import { ToastrService } from 'ngx-toastr';
import { BulkSentNotificationContext } from 'app/main/shared/types/SocketEventContext.type';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavbarSupportHubspotComponent } from './navbar-support-hubspot/navbar-support-hubspot.component';
import { NavbarCustomSupportComponent } from './navbar-custom-support/navbar-custom-support.component';
import { PlanConsumptionComponent } from 'app/layout/components/plan-consumption/plan-consumption.component';
import { PlanProvisioningComponent } from 'app/layout/components/plan-provisioning/plan-provisioning.component';
import { CompanyConfigService } from 'app/main/services/companyConfig.service';
import { ThemeConfigService } from 'app/main/services/theme-config.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;
  public user: User;
  public roleName = roleName;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  public subscription;
  public companyConfig: any;
  public themeConfig: any;
  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop > 100) &&
      this.coreConfig.layout?.navbar?.type == 'navbar-static-top' &&
      this.coreConfig.layout?.type == 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    private _auth: AuthenticationService,
    private _company: CustomerService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    public _subscription: SubscriptionService,
    private _io: SocketIoService,
    private _toastr: ToastrService,
    private readonly _cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private _companyConfigService: CompanyConfigService,
    private _themeConfigService: ThemeConfigService
  ) {
    this._auth.currentUser.subscribe((user) => {
      this.user = user;
      this._cdr.markForCheck();
      if (user) {
        if (isSender(user.role)) this.getSubscription();
        this.loadCompanyConfig();
        this.loadThemeConfig();
      }
    });

    this._io.listen(WEBSOCKET_EVENT.UPDATE_SUBSCRIPTION).subscribe(
      (context) => {
        if (context.companyId === this._auth.companyId) {
          console.log('IO UPDATE SMS SUBSCRIPTION');
          this.getSubscription();
        }
      },
      (err: HttpErrorResponse) =>
        console.error('Error listen subscription socket: ', err)
    );

    this._io.listen(WEBSOCKET_EVENT.BULK_SENT_NOTIFICATION).subscribe(
      (context: BulkSentNotificationContext) => {
        console.log('IO BULK SEND NOTIFICATION', context);
        if (context.companyId === this._auth.companyId) {
          this._toastr.info(context.message, 'Envio masivo realizado', {
            timeOut: 15000,
          });
        }
      },
      (err: HttpErrorResponse) =>
        console.error('Error listen subscription socket: ', err)
    );

    this.getSubscription();
    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us',
      },
      fr: {
        title: 'French',
        flag: 'fr',
      },
      de: {
        title: 'German',
        flag: 'de',
      },
      pt: {
        title: 'Portuguese',
        flag: 'pt',
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  getSubscription = (): void => {
    this._company.getCompanySubscription().subscribe(
      (sub) => {
        this.subscription = sub;
        this._cdr.markForCheck();
      },
      (err: HttpErrorResponse) =>
        console.error('Error al obtener subscripcion', err)
    );
  };

  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig(
      { app: { appLanguage: language } },
      { emitEvent: true }
    );
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig(
        { layout: { skin: 'dark' } },
        { emitEvent: true }
      );
    }
  }

  async logout(): Promise<void> {
    if (await this._auth.logout()) this._io.disconnect();
  }

  ngOnInit(): void {
    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
        this.horizontalMenu = config.layout.type === 'horizontal';
        this.hiddenMenu = config.layout.menu.hidden === true;
        this.currentSkin = config.layout.skin;

        // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
        if (this.coreConfig.layout?.type === 'vertical') {
          setTimeout(() => {
            if (this.coreConfig.layout.navbar.type === 'fixed-top') {
              this.isFixed = true;
            }
          }, 0);
        }
      });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout?.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
          if (isFixedTop) {
            this.isFixed = false;
          } else {
            this.isFixed = true;
          }
        });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });
  }

  getInitials = (): string => {
    if (!this.user) return '';
    return (
      String(this.user.first_name[0]).toUpperCase() +
      String(this.user.last_name[0]).toUpperCase()
    );
  };

  getName = (): string => {
    if (!this.user) return;
    return (
      this.user.first_name?.split(' ')[0] +
      ' ' +
      this.user.last_name?.split(' ')[0]
    );
  };

  loadCompanyConfig = (): void => {
    if (this._auth.companyOwnerId) {
      this._companyConfigService
        .getCompanyConfig(this._auth.companyOwnerId)
        .subscribe({
          next: (config) => {
            this.companyConfig = config;
            this._cdr.markForCheck();
          },
          error: (error) => {
            console.error('Error al cargar configuración de empresa:', error);
          },
        });
    }
  };

  loadThemeConfig = (): void => {
    console.log('=== CARGANDO THEME CONFIG ===');
    console.log('_auth.companyId:', this._auth.companyOwnerId);

    if (this._auth.companyOwnerId) {
      this._company.getCompanyById(this._auth.companyOwnerId).subscribe({
        next: (company) => {
          console.log('Company loaded:', company);
          console.log('company.theme_config_id:', company.theme_config_id);

          if (company.theme_config_id) {
            this._themeConfigService
              .getById(company.theme_config_id)
              .subscribe({
                next: (themeConfig) => {
                  console.log('ThemeConfig loaded:', themeConfig);
                  this.themeConfig = themeConfig;
                  this._cdr.markForCheck();
                },
                error: (error) => {
                  console.error(
                    'Error al cargar configuración de tema:',
                    error
                  );
                },
              });
          } else {
            console.log('No theme_config_id found for company');
          }
        },
        error: (error) => {
          console.error('Error al cargar empresa:', error);
        },
      });
    } else {
      console.log('No companyId found in auth');
    }
  };

  openHubSpotForm = () => {
    // Verificar si el usuario debe ver soporte personalizado
    if (this.shouldShowCustomSupport()) {
      this.openCustomSupport();
    } else {
      this.modalService.open(NavbarSupportHubspotComponent);
    }
  };

  shouldShowCustomSupport = (): boolean => {
    // Solo mostrar soporte personalizado si:
    // 1. La configuración está habilitada
    // 2. El usuario es subadmin o sender (no admin)
    // 3. El usuario pertenece a la empresa que tiene la configuración
    return (
      this.themeConfig?.customSupportEnabled &&
      (this.isSender || this.isSubAdmin)
    );
  };

  openCustomSupport = () => {
    const modalRef = this.modalService.open(NavbarCustomSupportComponent, {
      size: 'xs',
      windowClass: 'custom-support-modal',
    });

    modalRef.componentInstance.customSupportContent =
      this.themeConfig?.customSupportContent || '';
    modalRef.componentInstance.customSupportChannels =
      this.themeConfig?.customSupportChannels || [];
    modalRef.componentInstance.isCustomSupportContent =
      this.themeConfig?.isCustomSupportContent || false;
    modalRef.componentInstance.companyLogo = this.themeConfig?.logo || '';
  };

  getNextInvoiceDate = (dayNumber) => {
    const currentTime = now(this._auth.companyTz);
    const targetDate =
      currentTime.date() >= dayNumber
        ? currentTime.clone().add(1, 'month')
        : currentTime.clone();
    return targetDate.date(dayNumber).locale('es').format('DD-MMMM-YYYY') || '';
  };

  openPlanPrivisioning = () => {
    this.modalService.open(PlanProvisioningComponent, {
      windowClass: 'rounded',
      size: 'xs',
    });
  };

  openPlanConsumption = () => {
    this.modalService.open(PlanConsumptionComponent, {
      windowClass: 'p-2',
    });
  };

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  get tooltipSupportTitle(): string {
    if (this.user?.role === 'super_admin' || this.user?.role === 'admin') {
      return 'Reportar incidencia';
    }

    if (this.shouldShowCustomSupport()) {
      return 'Soporte';
    } else {
      return 'Reportar incidencia';
    }
  }

  get isAdmin(): boolean {
    return this._auth.isAdmin;
  }

  get isSAdmin(): boolean {
    return this._auth.isSuperAdmin;
  }

  get isSubAdmin(): boolean {
    return this._auth.isSubAdmin;
  }

  get isSender(): boolean {
    return this._auth.isSender;
  }
}
